/** Inject compiled motion CSS + host nodes. Browser-only. */

import { inferMotionMotif, motifGlyphs, motifWantsGlyphs } from "./motion-motif";
import { compileMotionCss, motionHostPlan } from "./compile-motion-css";
import { compileCssGraph, graphHostPlan } from "./css-graph";
import { compileMediaMockup } from "./media-mockup";
import type { MotionSpec } from "./motion-spec";
import {
  cellCenter,
  enrichScene,
  inferMapFromBrief,
  objectsFromPlan,
  type SceneSample,
} from "./scene-from-still";
import {
  FLOW_KEYFRAMES_CSS,
  flowInstances,
  flowStamp,
  parseMotionFlow,
  pathDurationSec,
} from "./motion-flow";

const STYLE_ID = "sf-motion-compiled";

export function applyMotionCss(
  spec: MotionSpec,
  opts?: { root?: HTMLElement; wallpaperUrl?: string; videoUrl?: string; plateUrl?: string },
): void {
  if (typeof document === "undefined") return;
  const root = opts?.root || document.documentElement;
  const mockup = spec.media?.mockup;
  const scene = spec.media?.scene ? enrichScene(spec.media.scene) : undefined;
  const videoUrl = opts?.videoUrl || spec.media?.videoUrl;
  const css = mockup
    ? compileMediaMockup(spec.id, mockup, {
        hasStill: Boolean(opts?.wallpaperUrl),
        hasVideo: Boolean(videoUrl),
        hasPlate: Boolean(opts?.plateUrl),
        hasScene: Boolean(scene),
        hasObjects: Boolean(spec.media?.object || spec.media?.mockup),
        playLayer: "css",
      })
    : spec.graph?.ops?.length
      ? compileCssGraph(spec.graph, `[data-bg-fx="${spec.id}"]`)
      : compileMotionCss(spec);
  let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
  root.setAttribute("data-bg-fx", spec.id);
  if (opts?.wallpaperUrl && (opts.wallpaperUrl.startsWith("https://") || opts.wallpaperUrl.length < 12_000)) {
    root.style.setProperty("--sf-wallpaper", `url("${opts.wallpaperUrl.replace(/"/g, "")}")`);
  }
  if (opts?.plateUrl) {
    root.style.setProperty("--sf-motion-plate", `url("${opts.plateUrl.replace(/"/g, "")}")`);
  }

  const hostParent = opts?.root || document.body;
  let host = hostParent.querySelector(":scope > .sf-motion-host") as HTMLElement | null;
  if (!host) {
    host = document.createElement("div");
    host.className = "sf-motion-host";
    host.setAttribute("aria-hidden", "true");
    hostParent.insertBefore(host, hostParent.firstChild);
  }
  host.replaceChildren();
  if (mockup) {
    if (opts?.wallpaperUrl) {
      const still = document.createElement("img");
      still.className = "sf-g-still";
      still.src = opts.wallpaperUrl;
      still.alt = "";
      still.draggable = false;
      host.appendChild(still);
    }
    if (opts?.plateUrl) {
      const plate = document.createElement("div");
      plate.className = "sf-g-plate";
      host.appendChild(plate);
    }
    if (videoUrl) {
      const vid = document.createElement("video");
      vid.className = "sf-g-video";
      vid.src = videoUrl;
      vid.autoplay = true;
      vid.muted = true;
      vid.loop = true;
      vid.playsInline = true;
      vid.setAttribute("playsinline", "");
      host.appendChild(vid);
      void vid.play().catch(() => undefined);
    }
    if (spec.media?.flow) {
      mountFlow(host, parseMotionFlow(spec.media.flow, spec.prompt || spec.name));
    } else {
      if (scene) mountScene(host, scene);
      const pick = spec.media?.object
        ? {
            object: spec.media.object,
            path: spec.media.path || inferMapFromBrief(spec.prompt || spec.name).path,
            density: spec.media.density ?? 0.5,
          }
        : spec.media?.mockup
          ? inferMapFromBrief(spec.prompt || spec.name)
          : null;
      if (pick) mountObjects(host, pick.object, pick.path, pick.density);
    }
    if (mockup.veil > 0.02) {
      const veil = document.createElement("div");
      veil.className = "sf-g-veil";
      host.appendChild(veil);
    }
    if (mockup.grain > 0.02) {
      const grain = document.createElement("div");
      grain.className = "sf-g-grain";
      host.appendChild(grain);
    }
    if (mockup.vignette > 0.02) {
      const vig = document.createElement("div");
      vig.className = "sf-g-vignette";
      host.appendChild(vig);
    }
    return;
  }
  const plan = spec.graph?.ops?.length ? graphHostPlan(spec.graph) : motionHostPlan(spec);
  for (const node of plan) {
    const n = node.count ?? 1;
    const alpha = "alphabet" in node ? node.alphabet : "";
    const attrs = "attrs" in node ? node.attrs : undefined;
    for (let i = 0; i < n; i++) {
      const el = document.createElement("span");
      el.className = node.className;
      if (attrs) {
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      }
      el.style.left = node.count ? `${(i * 37 + 7) % 94}%` : el.style.left;
      el.style.top = node.count ? `${(i * 23 + 9) % 86}%` : el.style.top;
      el.style.animationDelay = `${((i * 0.37) % 3).toFixed(2)}s`;
      if (alpha) el.textContent = alpha[i % alpha.length] || "";
      host.appendChild(el);
    }
  }
  if (spec.graph?.ops.length) return;
  const motif = inferMotionMotif(spec.prompt, spec.name, spec.blurb, spec.id);
  const chars = (spec.glyphs?.chars.split("") || motifGlyphs(motif)).filter(Boolean);
  const n = spec.glyphs?.count || (motifWantsGlyphs(motif) ? 28 : 0);
  if (chars.length && n) {
    for (let i = 0; i < n; i++) {
      const el = document.createElement("span");
      el.className = "sf-fx-glyph";
      el.dataset.motif = motif;
      el.textContent = chars[i % chars.length]!;
      el.style.left = `${(i * 19 + 3) % 96}%`;
      el.style.top = `${(i * 13 + 8) % 80}%`;
      el.style.animationDelay = `${((i * 0.29) % 2.4).toFixed(2)}s`;
      el.style.fontSize = `${11 + (i % 8)}px`;
      host.appendChild(el);
    }
  }
}

const FLOW_STYLE_ID = "sf-flow-compiled";

function mountFlow(
  host: HTMLElement,
  flow: ReturnType<typeof parseMotionFlow>,
): void {
  let tag = document.getElementById(FLOW_STYLE_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = FLOW_STYLE_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = FLOW_KEYFRAMES_CSS();
  const stamp = flowStamp(flow);
  const dur = pathDurationSec(flow.path, flow.speed);
  for (const o of flowInstances(flow)) {
    const el = document.createElement("img");
    el.className = "sf-flow-p";
    el.src = stamp;
    el.alt = "";
    el.draggable = false;
    el.dataset.path = flow.path;
    el.style.left = `${o.x}%`;
    el.style.top = `${o.y}%`;
    el.style.width = `${flow.size}px`;
    el.style.height = `${flow.size}px`;
    el.style.animationDuration = `${dur}s`;
    el.style.animationDelay = o.delay;
    host.appendChild(el);
  }
}

function mountObjects(
  host: HTMLElement,
  object: Parameters<typeof objectsFromPlan>[0],
  path: Parameters<typeof objectsFromPlan>[1],
  density: number,
): void {
  for (const o of objectsFromPlan(object, path, density)) {
    const el = document.createElement("span");
    el.className = "sf-g-obj";
    el.dataset.kind = o.kind;
    el.dataset.path = o.path;
    el.style.left = `${o.x}%`;
    el.style.top = `${o.y}%`;
    el.style.animationDelay = o.delay;
    el.style.opacity = String(0.45 + o.den * 0.5);
    host.appendChild(el);
  }
}

function mountScene(host: HTMLElement, scene: SceneSample): void {
  const map = enrichScene(scene);
  const grid = document.createElement("div");
  grid.className = "sf-g-scene";
  grid.style.gridTemplateColumns = `repeat(${map.cols}, 1fr)`;
  grid.style.gridTemplateRows = `repeat(${map.rows}, 1fr)`;
  map.cells.forEach((color, i) => {
    const cell = document.createElement("div");
    cell.className = "sf-g-cell";
    cell.dataset.kind = map.kinds[i];
    cell.dataset.path = map.paths[i];
    cell.dataset.edge = map.edge[i] ? "1" : "0";
    cell.style.setProperty("--den", String(map.density[i] ?? 0.4));
    cell.style.setProperty("--cell", color);
    grid.appendChild(cell);
  });
  host.appendChild(grid);
  let wisps = 0;
  for (let i = 0; i < map.edge.length && wisps < 40; i++) {
    if (!map.edge[i] || map.kinds[i] === "void") continue;
    const pos = cellCenter(i, map.cols, map.rows);
    const el = document.createElement("span");
    el.className = "sf-g-wisp";
    el.dataset.kind = map.kinds[i];
    el.style.left = `${pos.x}%`;
    el.style.top = `${pos.y}%`;
    el.style.animationDelay = `${((i * 0.17) % 3).toFixed(2)}s`;
    host.appendChild(el);
    wisps += 1;
  }
}

export function clearMotionCss(root?: HTMLElement): void {
  if (typeof document === "undefined") return;
  document.getElementById(STYLE_ID)?.remove();
  const r = root || document.documentElement;
  r.removeAttribute("data-bg-fx");
  r.style.removeProperty("--sf-wallpaper");
  r.style.removeProperty("--sf-motion-plate");
  (root || document.body).querySelector(":scope > .sf-motion-host")?.remove();
}
